import type { IncidentEntity, ReportEntity } from '@data';
import { fullDate } from '@app/utils/helpers/date-utils';
import { QUARTERS, quarterOf, type Quarter } from './quarterCosts';
import {
  AGREEMENT_TERMS,
  FLAT_FEE,
  serviceHoursFor,
  type OutOfHoursBand,
  type Priority,
  type ServiceHours,
  type SoldSupport,
} from './serviceLevels';

/** One incident's time in one quarter, priced the way support is charged. */
export interface IncidentCostLine {
  /** The incident, split further by band and by agreement, so one line carries one multiplier. */
  key: string;
  incidentId: string;
  title: string;
  priority: Priority | null;
  hours: number;
  /** The same hours at the team's plain rate. Null when someone who worked on it has no rate. */
  base: number | null;
  priorityRate: number;
  /** The out-of-hours bands its entries fell in, each at its own multiplier. */
  bands: { band: OutOfHoursBand; rate: number }[];
  cost: number | null;
}

export interface IncidentCostQuarter {
  quarter: Quarter;
  hours: number;
  /** What the priced lines came to, or null when none of them has a rate. */
  cost: number | null;
  lines: IncidentCostLine[];
}

const SERVICE_ZONE = 'Europe/Brussels';
const inServiceZone = new Intl.DateTimeFormat('en-GB', {
  timeZone: SERVICE_ZONE,
  weekday: 'short',
  hour: '2-digit',
  hourCycle: 'h23',
});

const hourOf = (clock: string): number => Number(clock.slice(0, 2));

/** The band an hour is charged in, or null inside the window the agreement covers. */
export const bandAt = (startedAt: string, service: ServiceHours, priority: Priority | null): OutOfHoursBand | null => {
  const terms = AGREEMENT_TERMS[service.agreement];
  if (terms.alwaysOnTopPriorities && (priority === 'p1' || priority === 'p2')) return null;
  const parts = inServiceZone.formatToParts(new Date(startedAt));
  const weekday = parts.find(part => part.type === 'weekday')?.value;
  const hour = Number(parts.find(part => part.type === 'hour')?.value);
  const isWeekend = weekday === 'Sat' || weekday === 'Sun';
  const inWindow = (!isWeekend || terms.days === 7) && hour >= hourOf(terms.from) && hour < hourOf(terms.to);
  if (inWindow) return null;
  if (weekday === 'Sun' || hour < 8) return 'night';
  // Anything else outside the window, such as 17:00 to 20:00 on Standard, is charged at the lighter band.
  return 'evening';
};

const round = (value: number): number => Math.round(value * 100) / 100;

interface Priced {
  hours: number;
  base: number | null;
  cost: number | null;
  band: OutOfHoursBand | null;
}

/** One report's incident time, at the team's rate times the priority and out-of-hours multipliers. */
export const priceIncidentReport = (
  report: ReportEntity,
  incident: IncidentEntity | undefined,
  rateAt: (day: string, memberId: string) => number | null,
  serviceAt: (day: string) => SoldSupport | null,
): {
  hours: number;
  base: number | null;
  cost: number | null;
  band: OutOfHoursBand | null;
  service: ServiceHours | null;
} => {
  const day = fullDate(report.workday);
  const priority = incident?.priority ?? null;
  // A product picked in the Team tab wins; otherwise the level the incident was logged under.
  const sold = serviceAt(day);
  // A flat fee has no multipliers, so its hours are charged at the plain rate.
  const service =
    sold?.agreement === FLAT_FEE
      ? null
      : (sold ?? (incident?.supportLevel ? serviceHoursFor(incident.supportLevel) : null));
  const hours = (report.minutes ?? 0) / 60;
  const memberId = report.involvement?.membership?.id;
  const rate = memberId ? rateAt(day, memberId) : null;
  const band = service && report.startedAt ? bandAt(report.startedAt, service, priority) : null;
  const multiplier = service ? (priority ? service.priorityRates[priority] : 1) * (band ? service.rates[band] : 1) : 1;
  const base = rate === null ? null : hours * rate;
  return { hours, base, cost: base === null ? null : base * multiplier, band, service };
};

/** Hours, multipliers and money per incident per quarter, oldest quarter first, empty quarters left out. */
export const incidentCostsByQuarter = ({
  reports,
  incidents,
  year,
  rateAt,
  serviceAt,
}: {
  reports: ReportEntity[];
  incidents: IncidentEntity[];
  year: string;
  rateAt: (day: string, memberId: string) => number | null;
  /** The agreement the product had on that day, or null when it had none. */
  serviceAt: (day: string) => SoldSupport | null;
}): IncidentCostQuarter[] => {
  const incidentById = new Map(incidents.map(incident => [incident.id, incident]));
  const byQuarter = new Map<
    Quarter,
    Map<string, { incidentId: string; priced: Priced[]; service: ServiceHours | null }>
  >();

  reports.forEach(report => {
    const day = report.incidentId ? fullDate(report.workday) : '';
    if (!report.incidentId || !day.startsWith(`${year}-`)) return;
    const incident = incidentById.get(report.incidentId);
    const { service, band, ...charge } = priceIncidentReport(report, incident, rateAt, serviceAt);

    const quarter = quarterOf(day);
    const lines = byQuarter.get(quarter) ?? new Map();
    // One line per band and set of rates, so the multiplier shown is always the one charged.
    const key = `${report.incidentId}|${band ?? ''}|${service ? JSON.stringify([service.priorityRates, service.rates]) : ''}`;
    const line = lines.get(key) ?? { incidentId: report.incidentId, priced: [], service };
    line.priced.push({ ...charge, band });
    lines.set(key, line);
    byQuarter.set(quarter, lines);
  });

  return QUARTERS.filter(quarter => byQuarter.has(quarter)).map(quarter => {
    const lines = [...(byQuarter.get(quarter)?.entries() ?? [])].map(([key, { incidentId, priced, service }]) => {
      const incident = incidentById.get(incidentId);
      const priority = incident?.priority ?? null;
      const unpriced = priced.some(each => each.base === null);
      const bands = [...new Set(priced.map(each => each.band).filter((band): band is OutOfHoursBand => !!band))];
      return {
        key,
        incidentId,
        title: incident?.title ?? '',
        priority,
        hours: round(priced.reduce((sum, each) => sum + each.hours, 0)),
        base: unpriced ? null : round(priced.reduce((sum, each) => sum + (each.base ?? 0), 0)),
        priorityRate: service && priority ? service.priorityRates[priority] : 1,
        bands: service ? bands.map(band => ({ band, rate: service.rates[band] })) : [],
        cost: unpriced ? null : round(priced.reduce((sum, each) => sum + (each.cost ?? 0), 0)),
      };
    });
    return {
      quarter,
      hours: round(lines.reduce((sum, line) => sum + line.hours, 0)),
      cost: lines.some(line => line.cost !== null)
        ? round(lines.reduce((sum, line) => sum + (line.cost ?? 0), 0))
        : null,
      lines,
    };
  });
};
